import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { projects } from './data';
import { initLenis, destroyLenis, getLenis } from './utils/lenis';
import './App.css';
import Loader from './components/Loader';
import Header from './components/Header';
import ProjectPage from './components/ProjectPage';
import Home from './pages/Home';
import Work, { WorkSide } from './pages/Work';
import About from './pages/About';
import Contact from './pages/Contact';

const Engineer = () => <WorkSide side="engineer" />;
const Design = () => <WorkSide side="design" />;

const pages = { home: Home, work: Work, engineer: Engineer, design: Design, about: About, contact: Contact };

const titles = {
  home: 'Temitope Abodunrin | Portfolio',
  work: 'Work · Temitope Abodunrin',
  engineer: 'Engineer · Temitope Abodunrin',
  design: 'Design · Temitope Abodunrin',
  about: 'About · Temitope Abodunrin',
  contact: 'Contact · Temitope Abodunrin',
};

// Every page lives behind the # in the address, so refreshing works on any host
function readRoute() {
  const hash = window.location.hash;
  const slug = hash.match(/^#\/work\/([\w-]+)$/)?.[1];
  const project = slug && projects.find(p => p.slug === slug);
  if (project) return { page: 'project', project };
  const name = hash.match(/^#\/([\w-]+)$/)?.[1];
  return { page: pages[name] ? name : 'home', project: null };
}

const jumpTo = top => {
  const lenis = getLenis();
  lenis?.resize();
  if (lenis) lenis.scrollTo(top, { immediate: true, force: true });
  else window.scrollTo(0, top);
};

function App() {
  // Greeting loader switched off for now (revisit later); to re-enable, start this as
  // `() => !window.matchMedia('(prefers-reduced-motion: reduce)').matches`
  const [showLoader, setShowLoader] = useState(false);
  const [route, setRoute] = useState(readRoute);
  const saved = useRef({});
  const shown = useRef(route);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    const onHash = () => {
      window.__siteNav = true;
      setRoute(current => {
        saved.current[current.page] = window.scrollY;
        return readRoute();
      });
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // A new page opens at its top; going back from a project returns to the spot you left
  useLayoutEffect(() => {
    const previous = shown.current;
    if (previous.page === route.page && previous.project === route.project) return;
    shown.current = route;
    const back = previous.page === 'project' && saved.current[route.page] != null;
    jumpTo(back ? saved.current[route.page] : 0);
    if (route.page !== 'project') document.title = titles[route.page];
  }, [route]);

  useEffect(() => {
    const lenis = initLenis();
    let frame;
    const raf = time => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      destroyLenis();
    };
  }, []);

  // Hold the page still while the loader plays
  useEffect(() => {
    if (showLoader) {
      getLenis()?.stop();
    } else {
      document.documentElement.classList.add('loaded');
      getLenis()?.start();
    }
  }, [showLoader]);

  const handleLoaded = useCallback(() => {
    document.documentElement.classList.add('loaded');
    getLenis()?.start();
    setTimeout(() => setShowLoader(false), 1000);
  }, []);

  // Rise-in for every .rv element on the page being shown
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          observer.unobserve(e.target);
        }
      }),
      { rootMargin: '0px 0px -8% 0px' }
    );
    document.querySelectorAll('.rv:not(.in)').forEach((el, i) => {
      el.style.transitionDelay = `${(i % 4) * 60}ms`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, [route]);

  const Page = pages[route.page];
  const key = route.page === 'project' ? route.project.slug : route.page;

  return (
    <>
      {showLoader && <Loader onDone={handleLoaded} />}
      <Header page={route.page} />
      <div key={key} className="page">
        {route.page === 'project' ? <ProjectPage project={route.project} /> : <Page />}
      </div>
    </>
  );
}

export default App;
